/** Tokenize an SVG `d` attribute into letters and numbers (Hanzi Writer uses absolute M/L/Q/Z). */

import { hasLength, isNonEmpty } from "@-/com.npmjs.ts-array-length";
import arrayLength from "@-/com.npmjs.extra-array__length";

function tokenizePathD(d: string): (string | number)[] {
  const out: (string | number)[] = [];
  let i = 0;
  while (i < d.length) {
    const ch = d[i];
    if (ch === undefined) {
      break;
    }
    if (ch === " " || ch === "\t" || ch === "\r" || ch === "\n" || ch === ",") {
      i++;
      continue;
    }
    if ((ch >= "A" && ch <= "Z") || (ch >= "a" && ch <= "z")) {
      out.push(ch);
      i++;
      continue;
    }
    let j = i;
    if (d[j] === "-" || d[j] === "+") {
      j++;
    }
    while (j < d.length) {
      const cj = d[j];
      if (cj === undefined) {
        break;
      }
      if (!(cj === "." || cj === "e" || cj === "E" || (cj >= "0" && cj <= "9"))) {
        break;
      }
      j++;
    }
    if (j === i) {
      i++;
      continue;
    }
    out.push(Number.parseFloat(d.slice(i, j)));
    i = j;
  }
  return out;
}

export type SvgPathSegment = { cmd: string; nums: number[] };

function readNum(tokens: (string | number)[], k: number): [number, number] {
  const t = tokens[k];
  if (typeof t !== "number" || !Number.isFinite(t)) {
    return [Number.NaN, k];
  }
  return [Math.round(t), k + 1];
}

/**
 * Parses `d` into absolute segments with all coordinates rounded to integers (Hanzi Writer
 * stroke paths use absolute uppercase commands).
 */
export function parseSvgPathToAbsoluteRoundedSegments(d: string): SvgPathSegment[] {
  const tokens = tokenizePathD(d);
  let k = 0;
  const segs: SvgPathSegment[] = [];

  const pushQ = (nums: number[]) => {
    segs.push({ cmd: "Q", nums });
  };

  while (k < arrayLength(tokens)) {
    const t = tokens[k];
    if (typeof t !== "string") {
      k++;
      continue;
    }
    const cmd = t;
    k++;
    if (cmd === "Z" || cmd === "z") {
      segs.push({ cmd: "Z", nums: [] });
      continue;
    }
    if (cmd === "M") {
      const [x0, k1] = readNum(tokens, k);
      const [y0, k2] = readNum(tokens, k1);
      if (!Number.isFinite(x0) || !Number.isFinite(y0)) {
        break;
      }
      k = k2;
      segs.push({ cmd: "M", nums: [x0, y0] });
      while (
        k + 1 < arrayLength(tokens) &&
        typeof tokens[k] === "number" &&
        typeof tokens[k + 1] === "number"
      ) {
        const [x, kx] = readNum(tokens, k);
        const [y, ky] = readNum(tokens, kx);
        if (!Number.isFinite(x) || !Number.isFinite(y)) {
          break;
        }
        k = ky;
        segs.push({ cmd: "L", nums: [x, y] });
      }
      continue;
    }
    if (cmd === "L") {
      const [x, k1] = readNum(tokens, k);
      const [y, k2] = readNum(tokens, k1);
      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        break;
      }
      k = k2;
      segs.push({ cmd: "L", nums: [x, y] });
      continue;
    }
    if (cmd === "H") {
      const [x, k1] = readNum(tokens, k);
      if (!Number.isFinite(x)) {
        break;
      }
      k = k1;
      segs.push({ cmd: "H", nums: [x] });
      continue;
    }
    if (cmd === "V") {
      const [y, k1] = readNum(tokens, k);
      if (!Number.isFinite(y)) {
        break;
      }
      k = k1;
      segs.push({ cmd: "V", nums: [y] });
      continue;
    }
    if (cmd === "Q") {
      const [x1, k1] = readNum(tokens, k);
      const [y1, k2] = readNum(tokens, k1);
      const [x2, k3] = readNum(tokens, k2);
      const [y2, k4] = readNum(tokens, k3);
      if (
        !Number.isFinite(x1) ||
        !Number.isFinite(y1) ||
        !Number.isFinite(x2) ||
        !Number.isFinite(y2)
      ) {
        break;
      }
      k = k4;
      pushQ([x1, y1, x2, y2]);
      while (
        k + 3 < arrayLength(tokens) &&
        typeof tokens[k] === "number" &&
        typeof tokens[k + 1] === "number" &&
        typeof tokens[k + 2] === "number" &&
        typeof tokens[k + 3] === "number"
      ) {
        const [qx1, ka] = readNum(tokens, k);
        const [qy1, kb] = readNum(tokens, ka);
        const [qx2, kc] = readNum(tokens, kb);
        const [qy2, kd] = readNum(tokens, kc);
        if (
          !Number.isFinite(qx1) ||
          !Number.isFinite(qy1) ||
          !Number.isFinite(qx2) ||
          !Number.isFinite(qy2)
        ) {
          break;
        }
        k = kd;
        pushQ([qx1, qy1, qx2, qy2]);
      }
      continue;
    }
    if (cmd === "C") {
      const nums: number[] = [];
      let ok = true;
      for (let n = 0; n < 6; n++) {
        const [v, kNext] = readNum(tokens, k);
        k = kNext;
        nums.push(v);
        if (!Number.isFinite(v)) {
          ok = false;
          break;
        }
      }
      if (!ok) {
        break;
      }
      segs.push({ cmd: "C", nums });
      while (
        k + 5 < arrayLength(tokens) &&
        typeof tokens[k] === "number" &&
        typeof tokens[k + 1] === "number" &&
        typeof tokens[k + 2] === "number" &&
        typeof tokens[k + 3] === "number" &&
        typeof tokens[k + 4] === "number" &&
        typeof tokens[k + 5] === "number"
      ) {
        const chunk: number[] = [];
        let chunkOk = true;
        for (let n = 0; n < 6; n++) {
          const [v, kNext] = readNum(tokens, k);
          k = kNext;
          chunk.push(v);
          if (!Number.isFinite(v)) {
            chunkOk = false;
            break;
          }
        }
        if (!chunkOk) {
          break;
        }
        segs.push({ cmd: "C", nums: chunk });
      }
      continue;
    }
    // Unknown command: skip one token to avoid an infinite loop
    k++;
  }

  return segs;
}

/** Rebuilds `d` with space-separated commands and integers (canonical for comparisons). */
export function svgPathSegmentsToD(segments: SvgPathSegment[]): string {
  const parts: string[] = [];
  for (const { cmd, nums } of segments) {
    parts.push(cmd);
    if (isNonEmpty(nums)) {
      parts.push(nums.join(" "));
    }
  }
  return parts.join(" ");
}

/** Sequential delta encoding: first value stored as-is, each next as (value - previous). */
export function encodeSequentialDeltas(values: number[]): number[] {
  const first = values[0];
  if (first === undefined) {
    return [];
  }
  const out: number[] = [first];
  for (let i = 1; i < arrayLength(values); i++) {
    const current = values[i];
    const previous = values[i - 1];
    if (current === undefined || previous === undefined) {
      break;
    }
    out.push(current - previous);
  }
  return out;
}

export function decodeSequentialDeltas(deltas: number[]): number[] {
  const first = deltas[0];
  if (first === undefined) {
    return [];
  }
  const out: number[] = [first];
  for (let i = 1; i < arrayLength(deltas); i++) {
    const previous = out[i - 1];
    const delta = deltas[i];
    if (previous === undefined || delta === undefined) {
      break;
    }
    out.push(previous + delta);
  }
  return out;
}

/** Flatten segment numeric arguments in order (commands stored separately). */
export function flattenSegmentNums(segments: SvgPathSegment[]): number[] {
  const flat: number[] = [];
  for (const { nums } of segments) {
    flat.push(...nums);
  }
  return flat;
}

export function unflattenSegmentNums(
  segmentsTemplate: SvgPathSegment[],
  flat: number[],
): SvgPathSegment[] {
  let j = 0;
  const out: SvgPathSegment[] = [];
  for (const template of segmentsTemplate) {
    const n = arrayLength(template.nums);
    const nums = flat.slice(j, j + n);
    j += n;
    if (!hasLength(nums, n)) {
      break;
    }
    out.push({ cmd: template.cmd, nums: [...nums] });
  }
  return out;
}

/** Command letters as ASCII codes (Z stored explicitly; no args). */
export function segmentCmdAsciiList(segments: SvgPathSegment[]): number[] {
  return segments.map(({ cmd }) => {
    const code = cmd.codePointAt(0);
    if (code === undefined) {
      throw new Error("SVG path segment has an empty command");
    }
    return code;
  });
}
